import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Alert as BaseAlert,
  Image,
  Button as BaseButton,
  Card as BaseCard,
  Line,
  H1,
  Typography
} from '../../../components';
import { Elevation } from '../../../utils';
import { startCall as startCallAction, stopCall as stopCallAction } from '../../../lib/redux';
import { ROUTES, NO_CALL_ONGOING, NO_CONNECTION_ESTABLISHED } from '../../../Constants';
import AlertIllustration from '../../../assets/images/illustrations/alert.svg';
import { JobBanner } from '../../../components/JobBanner';
import Scenario from './scenario';
import Controls from './controls';

const CallWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Col = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 60px;
  margin-bottom: 24px;
`;

const Card = styled(BaseCard)`
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 0;
`;

const SmallButton = styled(BaseButton)`
  max-width: 195px;
`;

const Alert = styled(BaseAlert)`
  left: 0;
  right: 0;
  top: 72px;
  z-index: ${Elevation.Alert};
`;

function renderAlert(hasError) {
  if (hasError) {
    switch (hasError) {
      case NO_CALL_ONGOING:
        return (
          <Alert error info fontWeight="bold">
            There is no call ongoing.
          </Alert>
        );
      case NO_CONNECTION_ESTABLISHED:
        return (
          <Alert error info fontWeight="bold">
            Browser connection was not established.
          </Alert>
        );
      default:
        return (
          <Alert error info fontWeight="bold">
            Could not stop the recording.
          </Alert>
        );
    }
  }
  return null;
}

function renderStartCallingAlert(hasError) {
  switch (hasError) {
    case NO_CALL_ONGOING:
      return 'There is no call ongoing';
    case NO_CONNECTION_ESTABLISHED:
      return 'Connection was not established';
    default:
      return 'Looks like we had a glitch';
  }
}

const Call = ({ task, isAvailable, history, location, startCall }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [hasError, setError] = useState(null);
  const [hasStopRecordingError, setRecordingError] = useState(false);

  const beforeUnload = event => {
    if (!event) {
      // eslint-disable-next-line
      event = window.event;
    }

    // eslint-disable-next-line no-param-reassign
    event.returnValue = 'There is an ongoing call. Are you sure you wish to reload?';
    // eslint-disable-next-line no-param-reassign
    event.cancelBubble = true;

    // eslint-disable-next-line no-alert
    if (event.stopPropagation) {
      event.stopPropagation();
      event.preventDefault();
    }

    return 'There is an ongoing call. Are you sure you wish to reload?';
  };

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnload);

    return function cleanup() {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);

  const retryCall = useCallback(() => {
    const onClick = async () => {
      try {
        await startCall(task.taskId, task.jobTypeCode);

        if (history.location && history.location.state && history.location.state.withError) {
          const state = { ...history.location.state };
          delete state.withError;
          history.replace({ ...history.location, state });
        }

        setRecordingError(false);
        setError(false);
        setIsRunning(false);
      } catch (err) {
        setError(true);
      }
    };

    onClick();
  });

  if (!isAvailable) {
    return <Redirect to={ROUTES.HOME} />;
  }

  if (hasError || (location.state && location.state.withError)) {
    return (
      <CallWrapper>
        <JobBanner />
        <Col>
          <Image src={AlertIllustration} />

          <H1 margin={{ top: 'large' }} fontWeight={700}>
            {renderStartCallingAlert(hasError)}
          </H1>

          <Typography margin={{ top: 'xs' }}>We apologize for the inconvenience</Typography>

          <Typography margin={{ bottom: 'large' }}>Please go ahead and try again</Typography>

          <SmallButton onClick={retryCall}>Try again</SmallButton>
        </Col>
      </CallWrapper>
    );
  }

  return (
    <>
      {renderAlert(hasStopRecordingError)}
      <CallWrapper>
        <JobBanner />
        <Card>
          <Scenario />
          <Line />
          <Controls
            isRunning={isRunning}
            setIsRunning={setIsRunning}
            setError={setError}
            setRecordingError={setRecordingError}
          />
        </Card>
      </CallWrapper>
    </>
  );
};

Call.propTypes = {
  isAvailable: PropTypes.bool.isRequired,
  startCall: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  task: PropTypes.shape({
    jobCall: PropTypes.string,
    taskId: PropTypes.string,
    callId: PropTypes.string,
    taskInput: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({
      withError: PropTypes.bool,
      recordingStatus: PropTypes.number
    })
  }).isRequired
};

Call.defaultProps = {
  task: {}
};

function mapStateToProps({ me, tasks, call }) {
  return {
    isAvailable: me.isAvailable,
    task: {
      ...tasks.currentTask.task,
      callId: call.callId
    }
  };
}

export default connect(
  mapStateToProps,
  {
    startCall: startCallAction,
    stopCall: stopCallAction
  }
)(withRouter(Call));
